import type { CardMessage, Effort, NativeTurn, ProviderId, Usage } from '../types';

export interface ToolDefinition {
  name: string;
  description: string;
  /** JSON Schema for the tool input. */
  inputSchema: Record<string, unknown>;
}

export interface ModelOption {
  id: string;
  label: string;
  /** Costs nothing to call (OpenRouter's free models). */
  free?: boolean;
  /** Supports tool calling, which Cue needs to propose changes. False means known not to. */
  tools?: boolean;
  contextLength?: number;
}

export interface StreamRequest {
  model: string;
  system: string;
  messages: CardMessage[];
  tools: ToolDefinition[];
  effort: Effort;
  /** Identifies the card, for providers that route a conversation's requests together (OpenRouter). */
  sessionId?: string;
  signal?: AbortSignal;
}

export type StreamEvent =
  | { type: 'text'; delta: string }
  /** A readable summary of the model's reasoning, streamed as it thinks. */
  | { type: 'reasoning'; delta: string }
  /** Tool input is still streaming. `partialJson` is everything received so far, so the UI can preview it. */
  | { type: 'tool-progress'; id: string; name: string; partialJson: string }
  | { type: 'tool-call'; id: string; name: string; input: Record<string, unknown> }
  /** The provider's own record of this turn, to store and replay to the same model next time. */
  | { type: 'native'; native: NativeTurn }
  | { type: 'usage'; usage: Usage }
  | { type: 'done'; stopReason: string };

export interface CompleteRequest {
  /** Defaults to the provider's utility model (cheap and fast). */
  model?: string;
  system: string;
  prompt: string;
  maxTokens?: number;
  signal?: AbortSignal;
}

export interface StructuredRequest extends CompleteRequest {
  /** A name for the output, used where the provider asks for one. */
  name: string;
  /** JSON Schema the output must match. */
  schema: Record<string, unknown>;
}

export type ProviderErrorKind = 'auth' | 'network' | 'rate-limit' | 'refusal' | 'aborted' | 'not-found' | 'unsupported' | 'other';

export class ProviderError extends Error {
  constructor(
    message: string,
    public readonly kind: ProviderErrorKind = 'other',
  ) {
    super(message);
    this.name = 'ProviderError';
  }
}

export interface Provider {
  readonly id: ProviderId;
  /** Cheap model used for side tasks such as titles and suggestions. */
  readonly utilityModel: string;
  stream(req: StreamRequest): AsyncGenerator<StreamEvent>;
  complete(req: CompleteRequest): Promise<string>;
  /** A JSON value matching the schema, using the provider's structured output support where it has it. */
  structured(req: StructuredRequest): Promise<unknown>;
  listModels(): Promise<ModelOption[]>;
}

export type { Usage };

export function isAbort(error: unknown): boolean {
  return (
    (error instanceof ProviderError && error.kind === 'aborted') ||
    (error instanceof DOMException && error.name === 'AbortError') ||
    (error instanceof Error && error.name === 'AbortError')
  );
}

/** Pull a JSON value out of model text that may wrap it in a code fence or prose. */
export function parseJsonLoosely(text: string): unknown {
  const fenced = /```(?:json)?\s*([\s\S]*?)```/i.exec(text);
  const candidate = (fenced?.[1] ?? text).trim();
  try {
    return JSON.parse(candidate);
  } catch {
    const start = candidate.search(/[[{]/);
    const end = Math.max(candidate.lastIndexOf('}'), candidate.lastIndexOf(']'));
    if (start >= 0 && end > start) {
      try {
        return JSON.parse(candidate.slice(start, end + 1));
      } catch {
        // fall through
      }
    }
    throw new ProviderError('The model did not return valid JSON.', 'other');
  }
}
